import React, { useRef } from "react";
import {
  emitRecommenderRequest,
  FromProduct,
  useExperiment,
} from "@catchoftheday/analytics";
import { getProductTiles } from "@catchoftheday/product-app";
import { RecommenderCarousel } from "../RecommenderCarousel";
import { PERSONALISED_RECOMMENDER_TITLE_EXPERIMENT } from "@common-vendors/consts";
import {
  getRecommenderTrackingData,
  handleRecommenderQueryUpdate,
  useVisibleRecommenderTiles,
} from "..";
import {
  usePersonalisedProductRecommendationsQuery,
  PersonalisedProductRecommendationsQuery,
} from "@common-vendors/generated/graphql";

type PersonalisedProductRecommenderProps = {
  adult: boolean;
  fromProduct: FromProduct;
  sourceId: string;
  sourceType: number;
};

export const PersonalisedProductRecommender = ({
  adult,
  fromProduct,
  sourceId,
  sourceType,
}: PersonalisedProductRecommenderProps) => {
  const {
    variants: [experiment],
    loading: experimentLoading,
  } = useExperiment([PERSONALISED_RECOMMENDER_TITLE_EXPERIMENT]);
  const visibleTiles = useVisibleRecommenderTiles();
  const trackingVariables = useRef({
    offset: 0,
    limit: visibleTiles,
  });
  const { data, error, loading, fetchMore } =
    usePersonalisedProductRecommendationsQuery({
      variables: { limit: visibleTiles, offset: 0, adult },
      skip: !visibleTiles,
      ssr: false,
      notifyOnNetworkStatusChange: true,
      onCompleted: ({
        personalisedRecommendedProducts: {
          metaData: {
            preHydrationCount,
            postHydrationCount,
            hydrationErrorCount,
          },
        },
      }) => {
        emitRecommenderRequest({
          source_type: sourceType,
          pre_hydration_product_count: preHydrationCount,
          post_hydration_product_count: postHydrationCount,
          hydration_error_count: hydrationErrorCount,
          page_offset: trackingVariables.current.offset,
          page_limit: trackingVariables.current.limit,
          ...fromProduct,
        });
      },
    });

  const heading =
    experiment?.bucket === 1
      ? "Suggested For You"
      : experiment?.bucket === 2
      ? "Just For You"
      : "Recommended For You";

  if (
    !data?.personalisedRecommendedProducts.products.length ||
    error ||
    experimentLoading
  ) {
    return null;
  }

  const products = data.personalisedRecommendedProducts.products;
  const { totalCount, nextItemIndex } =
    data.personalisedRecommendedProducts.pagination;

  const tiles = getProductTiles({
    products,
    sourceType,
    sourceId,
    fromProduct,
  });
  const trackingData = getRecommenderTrackingData(products, "product");

  return (
    <RecommenderCarousel
      tiles={tiles}
      heading={heading}
      sourceType={sourceType}
      trackingData={trackingData}
      fromProduct={fromProduct}
      paginate
      maxTiles={totalCount}
      carouselProps={{
        onSlideChange: ({ currentIndex, slidesPerView, direction }) => {
          const lastVisibleTile = currentIndex + slidesPerView - 1;
          if (direction === "forward" && !products[lastVisibleTile]) {
            trackingVariables.current = {
              offset: nextItemIndex,
              limit: slidesPerView,
            };
            fetchMore({
              variables: {
                offset: nextItemIndex,
              },
              updateQuery: (prevResult, { fetchMoreResult }) =>
                handleRecommenderQueryUpdate<PersonalisedProductRecommendationsQuery>(
                  {
                    prevResult,
                    fetchMoreResult,
                    recommenderKey: "personalisedRecommendedProducts",
                  }
                ),
            });
          }
        },
        disableNavButtons: {
          next: loading,
        },
        touchEnabled: !loading,
      }}
    />
  );
};
